import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash2, ShieldCheck, User, Check, X, Clock, Phone, Mail, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { TableState } from '@/components/DataState';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveUser, setApproveUser] = useState(null);
  const [addToSenders, setAddToSenders] = useState(true);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [usersRes, pendingRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/pending')
      ]);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
    } catch (err) { void 0; }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (userId, role) => {
    try {
      await api.put(`/users/${userId}/role`, { role });
      toast.success('Role updated');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openApproveDialog = (u) => {
    setApproveUser(u);
    setAddToSenders(!!u.phone_number);
  };

  const handleApprove = async () => {
    if (!approveUser) return;
    try {
      const { data } = await api.post(`/users/${approveUser._id}/approve`, { add_to_senders: addToSenders });
      let msg = 'User approved — notification email sent';
      if (data.sender_added) msg += '. Phone number added to approved senders.';
      toast.success(msg);
      setApproveUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject and remove this user? They will be notified.')) return;
    try {
      await api.post(`/users/${userId}/reject`);
      toast.success('User rejected and removed');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>User Management</h1>
        <p className="text-muted-foreground text-sm mt-1">{users.length} registered users</p>
      </div>

      {/* Pending Approvals */}
      {pendingUsers.length > 0 && (
        <div className="border border-amber-500/30 rounded-md overflow-hidden" data-testid="pending-users-section">
          <div className="flex items-center gap-2 px-5 py-3 bg-amber-500/10 border-b border-amber-500/30">
            <Clock className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Pending Approvals ({pendingUsers.length})</h3>
          </div>
          <div className="divide-y divide-border">
            {pendingUsers.map((u) => (
              <div key={u._id} className="flex items-center justify-between px-5 py-4 hover:bg-secondary/20 transition-colors" data-testid={`pending-user-${u._id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center text-xs font-semibold shrink-0">
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{u.name}</p>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground font-mono">{u.email}</span>
                      {u.phone_number && (
                        <span className="text-xs text-muted-foreground font-mono flex items-center gap-1">
                          <Phone className="w-3 h-3" />{u.phone_number}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`text-xs font-medium flex items-center gap-1 ${u.email_verified ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>
                        {u.email_verified ? <MailCheck className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                        {u.email_verified ? 'Email verified' : 'Email not verified'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Registered {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <Button size="sm" variant="outline" className="rounded-[4px] border-[hsl(var(--success))]/30 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10" onClick={() => openApproveDialog(u)} data-testid={`approve-user-${u._id}`}>
                    <Check className="w-3.5 h-3.5 mr-1.5" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-[4px] border-destructive/30 text-destructive hover:bg-destructive/10" onClick={() => handleReject(u._id)} data-testid={`reject-user-${u._id}`}>
                    <X className="w-3.5 h-3.5 mr-1.5" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Users */}
      <div className="border border-border rounded-md overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">User</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Phone</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Role</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Status</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden lg:table-cell">Joined</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <TableState loading={loading} empty={users.length === 0} colSpan={7} emptyText="No users">
                {users.map((u) => (
                  <tr key={u._id} className="hover:bg-secondary/30 transition-colors" data-testid={`user-row-${u._id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-semibold shrink-0">
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <span className="text-sm font-medium">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell font-mono">{u.email}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell font-mono">{u.phone_number || '—'}</td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => handleRoleChange(u._id, v)} data-testid={`role-select-${u._id}`}>
                        <SelectTrigger className="w-[110px] h-8 rounded-[4px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">
                            <div className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Admin</div>
                          </SelectItem>
                          <SelectItem value="user">
                            <div className="flex items-center gap-1.5"><User className="w-3 h-3" /> User</div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-col gap-0.5">
                        {u.role === 'admin' ? (
                          <span className="text-xs font-medium text-[hsl(var(--success))]">Admin</span>
                        ) : (
                          <>
                            <span className={`text-xs font-medium ${u.email_verified ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>
                              {u.email_verified ? 'Email verified' : 'Email pending'}
                            </span>
                            <span className={`text-xs font-medium ${u.admin_approved ? 'text-[hsl(var(--success))]' : 'text-amber-500'}`}>
                              {u.admin_approved ? 'Approved' : 'Awaiting approval'}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell font-mono">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(u._id)}
                        data-testid={`delete-user-${u._id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </TableState>
            </tbody>
          </table>
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={!!approveUser} onOpenChange={(open) => !open && setApproveUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Approve User
            </DialogTitle>
          </DialogHeader>
          {approveUser && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center text-sm font-semibold">
                  {approveUser.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-sm font-medium">{approveUser.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{approveUser.email}</p>
                </div>
              </div>

              {approveUser.phone_number && (
                <div className="flex items-start gap-3 p-3 border border-border rounded-md bg-secondary/30">
                  <Checkbox
                    id="add-to-senders"
                    checked={addToSenders}
                    onCheckedChange={(v) => setAddToSenders(!!v)}
                    data-testid="add-to-senders-checkbox"
                  />
                  <div>
                    <label htmlFor="add-to-senders" className="text-sm font-medium cursor-pointer">
                      Add WhatsApp number to approved senders
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Allow <span className="font-mono">{approveUser.phone_number}</span> to send receipts via WhatsApp
                    </p>
                  </div>
                </div>
              )}

              {!approveUser.phone_number && (
                <p className="text-xs text-muted-foreground bg-secondary/50 px-3 py-2 rounded-md">
                  This user did not provide a phone number. You can add their number to approved senders manually in Settings.
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setApproveUser(null)} className="rounded-[4px]" data-testid="approve-cancel-btn">Cancel</Button>
                <Button onClick={handleApprove} className="rounded-[4px]" data-testid="approve-confirm-btn">
                  <Check className="w-4 h-4 mr-2" /> Approve User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
